import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Container } from "@pixi/react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setIntroTransitionActive,
  setUserInterfaceDisabled,
} from "../../../../../../State/actions";
import { setLoaderComplete } from "../../../../../../State/Slices/loadingProgressSlice";
import { attemptFullscreenToggle, safeInit } from "../../../../../../utilities";
import TickerTimeout from "../../../../../../utils/TickerTimeout";
import { HowlCache } from "../../../../../../State/Slices/soundsSlice";
import {
  selectIntroPopupActive,
  selectUserInterfaceDisabled,
  selectIsPotCollectionMidBonusReload,
  selectCurrentRound,
  selectLoaderFadedOut,
  selectGameFullyLoaded,
} from "../../../../../../State/selectors";
import { SOUND_NAME, MUSIC_FADE_MS } from "../../../../../../sounds";
import {
  DimensionsContext,
  useDimensions,
} from "../../../../../../Contexts/DimensionsContext";
import {
  useInitialIdleAnimations,
} from "../../../../../../Hooks/useLandingAnimations";
import { useFreeSpinBasicInitialIdleAnimations } from "../../../../../../Hooks/useFreeSpinBasicLandingAnimations";
import store from "../../../../../../State/Store/store";
import { soundManager } from "../../../../../../SoundManager";
import { useSecondaryToolbar } from "../../../../../../Hooks/useSecondaryToolbar";
import { FEATURES_CONFIG } from "../../../../../../featuresConfig";
import { baseDiag } from "../../../../../../utils/fsDiag";
import IntroPopupComposedController from "./IntroPopupComposedController";
import ButtonSound from "../../../../secondary/Buttons/ButtonSound";

// Wait for the main thread to actually go quiet before animating the tap text
// in. Loading finishing flips loaderComplete, which mounts GameView +
// UserInterface + Transitions + Dialogs + WinAnimation in ONE commit; measured
// frames during that storm run 270-360ms. A fixed frame count is useless here
// (12 frames can elapse while the thread is still blocked) — instead require a
// run of genuinely cheap frames, which is the only reliable signal the work is
// done. Until then the spine keeps spinning, so the player sees a spinner
// rather than a stutter.
// "Cheap" has to be relative to the device, not an absolute figure. A fixed
// 24ms is roughly a 42fps floor: on any device that simply runs slower than
// that — plenty of phones, and a Mac driving a large canvas — NO frame ever
// qualified, the run never reached six, and every boot sat on the full timeout
// below before tap-to-start appeared. That was the "waiting for something"
// pause. So the bar is the device's own best frame in this wait, with 24ms as
// a floor (never demand better than that) and 60ms as a ceiling (never accept
// a frame that slow as evidence the storm is over).
const QUIET_FRAME_MS = 24;
const QUIET_FRAME_MAX_MS = 60;
const QUIET_FRAME_RATIO = 1.6; // headroom over the best frame seen
const QUIET_FRAMES_REQUIRED = 6;
// Never hang: if something keeps the thread busy indefinitely, reveal anyway
// rather than trapping the player on a popup they can't dismiss.
const QUIET_WAIT_MAX_MS = 2500;

// The intro music fades out as the popup starts to close. Keep it SHORT — the
// background music starts in the same handler, so this is also how long the
// two overlap.
const INTRO_MUSIC_FADE_MS = MUSIC_FADE_MS;



// ─── IntroPopupComposed ───────────────────────────────────────────────────────
// Slim React shell: handles Redux state, click/keyboard events, and lifecycle.
// All PIXI display objects (bg, logo, cards, text) are managed by the controller.
const IntroPopupComposed = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const introPopupActive = useSelector(selectIntroPopupActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);

  const dimensionsContext = useDimensions();
  const { dimensions, device, orientation, uiSymbolSize, isMobileSmall } = dimensionsContext;
  // const toolbar = useSecondaryToolbar();
  // // Match ToolbarTop: sound button is positioned relative to a centered toolbar.
  // // Without this offset, mobile landscape (toolbar narrower than screen) lands left of the real button.
  // const toolbarOffsetX = (dimensions.width - toolbar.width) / 2;
  // (Moved into IntroSoundButton, 2026-10-29 — it reads the iPad's column.)
  // ── An iPad upright: the sound button in the 9:16 column, where the HUD
  // has it (DimensionsContext uiBoxWidth, 2026-10-29) ── only the width is
  // the column's; landscape keeps the frame as before.
  const columnContext = useMemo(() => {
    const { uiBoxWidth } = dimensionsContext.dimensions;
    if (!uiBoxWidth || uiBoxWidth === dimensionsContext.dimensions.width) {
      return dimensionsContext;
    }
    return {
      ...dimensionsContext,
      dimensions: { ...dimensionsContext.dimensions, width: uiBoxWidth },
      // The HUD's mobile-small places there (hudDimensions).
      isMobileSmall: true,
    };
  }, [dimensionsContext]);
  const columnOffsetX =
    columnContext === dimensionsContext ? 0 : dimensions.uiBoxOffsetX || 0;
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  // The free-spins board's idles — a reload straight into the room.
  const { triggerFreeSpinBasicIdleAnimations } =
    useFreeSpinBasicInitialIdleAnimations();
  const isPotCollectionMidBonusReload = useSelector(selectIsPotCollectionMidBonusReload);
  const currentRound = useSelector(selectCurrentRound);
  const loaderFadedOut = useSelector(selectLoaderFadedOut);
  // This popup now appears BEFORE the game has finished loading, so it can't
  // assume everything is ready. Drives the popi-spine ↔ tap-text swap below.
  const gameFullyLoaded = useSelector(selectGameFullyLoaded);

  const containerRef = useRef(null);
  const controllerRef = useRef(null);
  // Always holds the latest dimensions — read when the deferred (RAF) controller
  // construction runs, so a resize during the loader fade isn't built from the
  // stale first-render dimensions captured by the init effect's [] closure.
  const dimensionsContextRef = useRef(dimensionsContext);
  dimensionsContextRef.current = dimensionsContext;
  // Read by the deferred (RAF) controller construction below — by the time it
  // runs, loading may ALREADY be finished, and an effect alone would miss it:
  // it fires before the controller exists and then never re-runs, because
  // gameFullyLoaded never changes again. That left the popup with no spine and
  // no tap text at all.
  const gameFullyLoadedRef = useRef(gameFullyLoaded);
  gameFullyLoadedRef.current = gameFullyLoaded;
  const introSoundPlayedRef = useRef(false);
  const revealScheduledRef = useRef(false);
  const handleClickRef = useRef(null);
  const handleSwipeFullscreenRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const pendingRafIdsRef = useRef([]);
  // Intro music is this popup's and nothing else's. Once the popup starts to
  // close it must never be heard again, by any path.
  const introMusicBlockedRef = useRef(false);
  const introMusicStopTimerRef = useRef(null);

  // ── Start the intro music ──────────────────────────────────────────────
  // Fires the moment the loader is gone and this screen takes over, NOT when
  // the game finishes settling — there were several seconds of silence between
  // the two. Only counts as played once the Howl actually exists: sounds load
  // near the end of boot, so an early call can arrive before the cache is
  // filled, and marking it played then would lose the music for good.
  const startIntroMusic = useCallback(() => {
    if (introSoundPlayedRef.current) return;
    if (introMusicBlockedRef.current) return;
    if (!HowlCache[SOUND_NAME.intro_music]) return;
    introSoundPlayedRef.current = true;
    soundManager.play(SOUND_NAME.intro_music);
  }, []);

  // Reveal the tap text once the game-tree mount has settled. Guarded so the
  // two callers (deferred construction below, and the loading effect) can't
  // both schedule it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scheduleTapReveal = useCallback(async () => {
    if (revealScheduledRef.current) return;
    revealScheduledRef.current = true;

    // 1. Mount the game tree straight away. It builds behind a fully opaque
    //    popup, so it costs nothing visually, and it overlaps with the
    //    wordmark finishing below instead of queueing after it — the two used
    //    to be serial, which added up to a full revolution (~2.9s) of dead
    //    time on top of the mount.
    dispatch(setLoaderComplete());

    // 2. Let the wordmark complete its current revolution — cutting it off
    //    mid-rotation reads as a glitch. It then holds its final frame. By the
    //    time this resolves the mount storm has usually already passed.
    await controllerRef.current?.awaitLoadingSpineComplete();
    if (!controllerRef.current) return; // closed while we were waiting

    const startedAt = performance.now();
    let lastFrameAt = startedAt;
    let quietFrames = 0;
    let bestFrame = Infinity;

    const tick = () => {
      const now = performance.now();
      const frameDuration = now - lastFrameAt;
      lastFrameAt = now;

      bestFrame = Math.min(bestFrame, frameDuration);
      const quietBar = Math.min(
        QUIET_FRAME_MAX_MS,
        Math.max(QUIET_FRAME_MS, bestFrame * QUIET_FRAME_RATIO),
      );
      quietFrames = frameDuration <= quietBar ? quietFrames + 1 : 0;
      const settled = quietFrames >= QUIET_FRAMES_REQUIRED;
      const timedOut = now - startedAt >= QUIET_WAIT_MAX_MS;

      if (!settled && !timedOut) {
        const nextId = requestAnimationFrame(tick);
        pendingRafIdsRef.current.push(nextId);
        return;
      }

      controllerRef.current?.setGameLoaded(true);
      // Fallback only — normally the music is already running from the
      // loader-faded beat above. This catches the case where the sound cache
      // was not filled yet at that point.
      startIntroMusic();
    };

    const id = requestAnimationFrame(tick);
    pendingRafIdsRef.current.push(id);
  }, [dispatch, startIntroMusic]);

  // ── Initialize controller once container is available ───────────────────
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new IntroPopupComposedController(
          containerRef.current,
          dimensionsContextRef.current,
          t,
        );
        // Wire click handler immediately via ref proxy — always calls latest version
        controllerRef.current.setClickHandler((...args) => {
          if (handleClickRef.current) handleClickRef.current(...args);
        });
        // Fullscreen-on-swipe proxy — enters fullscreen without closing the popup
        controllerRef.current.setFullscreenHandler((...args) => {
          if (handleSwipeFullscreenRef.current)
            handleSwipeFullscreenRef.current(...args);
        });
        // Always start on the spine — even when loading has already finished,
        // so the reveal is an animation rather than a cold start.
        controllerRef.current.setGameLoaded(false);
        if (gameFullyLoadedRef.current) scheduleTapReveal();
      });
    }
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Update controller dimensions on resize / orientation change ────────
  // ALWAYS, shown or not: every popup's tap word sits on the line this popup
  // measures and publishes (utils/tapWordAnchor). Skipping the layout while
  // it was closed (2026-10-09) left that line on the old screen size, and
  // the popups' tap words landed on it after a resize or rotation.
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateDimensions(dimensionsContext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device, orientation, isMobileSmall]);

  // ── Click / tap handler: fullscreen + dismiss popup ─────────────────
  const handleClick = useCallback(
    async (event) => {
      // Still loading — the tap text isn't even visible yet, but the whole
      // backdrop is a tap surface, so guard here too. Entering now would run
      // triggerInitialIdleAnimations() against reel controllers that don't
      // exist, and reveal a half-built game.
      if (!gameFullyLoaded) return;
      // ── ...and not until the tap word has finished ARRIVING ──
      //
      // `gameFullyLoaded` is the instant the reveal starts, not the instant it
      // ends. The word fades over 500ms and scales over 600ms, so the guard
      // above left 625ms in which the popup could be dismissed while it was
      // still growing — or before it had drawn at all, because the whole
      // backdrop is a tap surface, not just the text.
      //
      // Covers the spacebar too: the key handler below routes through this
      // same function rather than closing on its own.
      //
      // Cannot hang the boot. setGameLoaded(true) always runs — on settle or
      // on timeout — and _revealTapText sets the flag true outright when there
      // is no tap word to animate.
      if (controllerRef.current?.isTapReady?.() !== true) return;
      if (debounceTimeoutRef.current) return;
      debounceTimeoutRef.current = setTimeout(() => {
        debounceTimeoutRef.current = null;
      }, 300);

      if (event?.stopPropagation) event.stopPropagation();
      // soundManager.play(SOUND_NAME.tap_to_continue);
      // This game's own click (2026-10-20) — tap_to_continue is Mad Labs'.
      soundManager.play(SOUND_NAME.button_click);
      await attemptFullscreenToggle(dispatch, device);

      const hasWins = currentRound?.win > 0;
      baseDiag("idle on load — intro closed", {
        hasWins,
        idleEnabled: FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED,
        potMidBonus: !!isPotCollectionMidBonusReload,
      });

      if (FEATURES_CONFIG.POT_COLLECTION.ENABLED && isPotCollectionMidBonusReload) {
        // Mid-bonus reload: base game goes to idle (no win processing).
        // The bonus scene is already mounted (potCollectionActive = true).
        // GameView transition effect will handle POTs, counter, and respins popup.
        triggerInitialIdleAnimations();
      } else if (hasWins) {
        // Win round — skip idle, let win controller handle it
      // } else if (FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED) {
      // ── Not under a feature room (2026-10-08, the Mad Labs room rule) ──
      // Reloaded into free spins or Hold & Win, the base board is hidden
      // under them: live, the entry wipe snap-clears it to pictures and the
      // exit wipe starts its idles again (TransitionDialog). Started here,
      // its spines ran unseen for the whole bonus.
      } else if (
        FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED &&
        !store.getState().freeSpins?.active &&
        !store.getState().holdAndWin?.active
      ) {
        triggerInitialIdleAnimations();
      }

      // ── Reloaded INTO free spins: their board idles too (Vladimir,
      // 2026-10-08: "free spin symbols … are static … same thing as in base
      // game") ── the room is on screen from the first frame with no wipe, and
      // the wipe's midpoint (TransitionDialog) is the only other place its
      // idles start. The base rule, on the room's own round: a round that
      // replays its win leaves the board to the win presentation. Not under a
      // Hold & Win on screen — its exit starts them (TransitionDialog).
      {
        const st = store.getState();
        // ── A round with a win to replay idles TOO (Vladimir, 2026-10-08:
        // "symbols underneath are still static") ── unlike the base game, its
        // presentation waits behind the "FREE SPINS n LEFT" popup, so the
        // board sat as pictures; and live, a landed board idles first and the
        // win presentation plays over the idles anyway.
        if (
          FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED &&
          st.freeSpins?.active &&
          !st.holdAndWin?.active
          // && !st.freeSpins.currentRound?.triggerWin
        ) {
          // triggerFreeSpinBasicIdleAnimations();
          // A longer wait for the board than the wipe's 120 frames: it plays
          // as soon as the room's spines are built, it just never gives up
          // early on a slow device.
          triggerFreeSpinBasicIdleAnimations(600);
        }
      }

      // The intro music belongs to this popup and to nothing else. Fade it out
      // FAST as the close begins — the background music starts on the line
      // below, so a long fade is a long overlap.
      //
      // The fade alone is NOT enough. This click is usually the first user
      // gesture of the session, and that gesture is what unblocks browser
      // audio — so Howler can still report the track as not playing at this
      // instant. soundManager.fadeOut() returns early on a sound that is not
      // playing, the fade no-ops, and the track then starts ON the gesture and
      // loops for ever with nothing left to stop it. So: block any further
      // start, fade for the ear, and hard-stop once the fade is done.
      introMusicBlockedRef.current = true;
      soundManager.fadeOut(SOUND_NAME.intro_music, INTRO_MUSIC_FADE_MS);
      introMusicStopTimerRef.current?.clear();
      introMusicStopTimerRef.current = new TickerTimeout(() => {
        soundManager.stop(SOUND_NAME.intro_music);
        introMusicStopTimerRef.current = null;
      }, INTRO_MUSIC_FADE_MS + 50);

      // Start background music now that intro is closing
      soundManager.releaseMusicPlayback();

      dispatch(setIntroTransitionActive(true));
    },
    [
      dispatch,
      device,
      triggerInitialIdleAnimations,
      triggerFreeSpinBasicIdleAnimations,
      isPotCollectionMidBonusReload,
      currentRound,
      gameFullyLoaded,
    ],
  );

  // ── Keep click handler ref current (avoids stale closures) ─────────────
  handleClickRef.current = handleClick;

  // ── Swipe fullscreen: enter fullscreen (mobile/tablet) without closing ──
  // attemptFullscreenToggle only enters when not already fullscreen, so
  // repeated swipes are safe and never toggle it back off.
  const handleSwipeFullscreen = useCallback(() => {
    attemptFullscreenToggle(dispatch, device);
  }, [dispatch, device]);
  handleSwipeFullscreenRef.current = handleSwipeFullscreen;

  // ── Enable UI when popup becomes active ────────────────────────────────
  useEffect(() => {
    if (introPopupActive) {
      dispatch(setUserInterfaceDisabled(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introPopupActive]);

  // ── Play card entrance animation after loader fades out ────────────────
  useEffect(() => {
    if (introPopupActive && loaderFadedOut && controllerRef.current) {
      controllerRef.current.playIntro();
      startIntroMusic();
    }
  }, [introPopupActive, loaderFadedOut, startIntroMusic]);

  // ── Loading state: popi spine ⇄ tap-to-start ───────────────────────────
  // While the rest of the game downloads, the preloader spine holds the
  // tap-text slot and entry stays blocked. When loading completes the spine is
  // destroyed, the tap text appears, and the intro sound plays — sounds are
  // loaded near the end of the boot, so this is also the first moment they can
  // actually be heard.
  // Handles the case where loading finishes AFTER the controller exists. The
  // already-loaded-at-construction case is covered in the init effect above.
  useEffect(() => {
    if (!controllerRef.current || !gameFullyLoaded) return;
    scheduleTapReveal();
  }, [gameFullyLoaded, scheduleTapReveal]);

  // ── The intro music never outlives this popup ──────────────────────────
  // A remount, an error, or a close that never reaches handleClick must not
  // leave it looping under the game.
  useEffect(() => {
    return () => {
      introMusicBlockedRef.current = true;
      introMusicStopTimerRef.current?.clear();
      introMusicStopTimerRef.current = null;
      soundManager.stop(SOUND_NAME.intro_music);
    };
  }, []);

  // ── Cleanup pending RAFs on close ──────────────────────────────────────
  useEffect(() => {
    if (!introPopupActive) {
      pendingRafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
      pendingRafIdsRef.current = [];
    }
  }, [introPopupActive]);

  // ── Spacebar listener (identical to original IntroPopupComposed) ───────
  useEffect(() => {
    const handleKeyDown = (event) => {
      const isSpace =
        event.code === "Space" || event.key === " " || event.keyCode === 32;
      const isLeft =
        event.code === "ArrowLeft" ||
        event.key === "ArrowLeft" ||
        event.keyCode === 37;
      const isRight =
        event.code === "ArrowRight" ||
        event.key === "ArrowRight" ||
        event.keyCode === 39;
      if (!isSpace && !isLeft && !isRight) return;
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function") event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
        return;
      }
      if (!introPopupActive || userInterfaceDisabled) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      // ArrowLeft / ArrowRight move the slider one slide; Space closes.
      if (isLeft) {
        controllerRef.current?.prevSlide();
      } else if (isRight) {
        controllerRef.current?.nextSlide();
      } else {
        handleClick(event);
      }
    };

    if (introPopupActive && !userInterfaceDisabled) {
      document.addEventListener("keydown", handleKeyDown, { capture: true, passive: false });
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [introPopupActive, userInterfaceDisabled, handleClick]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      pendingRafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
      pendingRafIdsRef.current = [];
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────
  // Container always mounts; visibility toggled by introPopupActive.
  // Controller manages all PIXI children imperatively.
  return (
    <Container
      ref={containerRef}
      name="intro_composed"
      x={0}
      y={0}
      zIndex={100}
      visible={introPopupActive}
      eventMode={introPopupActive ? "static" : "none"}
      hitArea={{ contains: () => true }}
      sortableChildren={true}
    >
      {/* Sound toggle — same parent offset as ToolbarTop so coords match in-game ButtonSound */}
      {introPopupActive && (
        // <Container
        //   name="intro_sound_button_anchor"
        //   x={toolbarOffsetX}
        //   y={0}
        //   zIndex={200}
        // >
        //   <ButtonSound toolbar={toolbar} zIndex={200} />
        // </Container>
        <Container name="intro_sound_button_column" x={columnOffsetX} zIndex={200}>
          <DimensionsContext.Provider value={columnContext}>
            <IntroSoundButton />
          </DimensionsContext.Provider>
        </Container>
      )}
    </Container>
  );
};

// The sound toggle, as ToolbarTop places it: relative to a centred toolbar
// (mobile landscape's is narrower than the screen), read from the context it
// is given — the iPad's 9:16 column in portrait.
const IntroSoundButton = () => {
  const { dimensions } = useDimensions();
  const toolbar = useSecondaryToolbar();
  const toolbarOffsetX = (dimensions.width - toolbar.width) / 2;
  return (
    <Container name="intro_sound_button_anchor" x={toolbarOffsetX} y={0} zIndex={200}>
      <ButtonSound toolbar={toolbar} zIndex={200} />
    </Container>
  );
};

export default IntroPopupComposed;
